import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { plaqueStatusLabel } from '../lib/plaqueStatus';
import { toast } from 'sonner';

const STATUS_TABS = [
  { id: 'pending_admin_fulfillment', label: 'Order Received' },
  { id: 'processing',                 label: 'In Review' },
  { id: 'ordered_with_supplier',      label: 'In Production' },
  { id: 'shipped',                    label: 'Shipped' },
  { id: 'delivered',                  label: 'Delivered' },
  { id: 'cancelled',                  label: 'Cancelled' },
  { id: 'refunded',                   label: 'Refunded' },
  { id: 'all',                        label: 'All' },
];

const STATUS_PILL = {
  pending_admin_fulfillment: 'bg-amber-50 text-amber-700 border border-amber-200',
  processing:                'bg-sky-50 text-sky-700 border border-sky-200',
  ordered_with_supplier:     'bg-indigo-50 text-indigo-700 border border-indigo-200',
  shipped:                   'bg-violet-50 text-violet-700 border border-violet-200',
  delivered:                 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  cancelled:                 'bg-red-50 text-red-700 border border-red-200',
  refunded:                  'bg-gray-100 text-gray-600 border border-gray-300',
};

function money(usd) {
  if (usd === null || usd === undefined) return '—';
  return `$${Number(usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortDate(iso) {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

export default function AdminPlaqueOrders() {
  const [statusFilter, setStatusFilter] = useState('pending_admin_fulfillment');
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/plaque-orders?status=${statusFilter}`);
      setOrders(data.orders || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (focusId) setExpandedId(focusId);
  }, [focusId]);

  const markProcessing = async (o) => {
    if (!window.confirm(`Mark order ${o.order_id.replace('plaque_', '')} as In Review?`)) return;
    try {
      await api.post(`/admin/plaque-orders/${o.order_id}/status`, { status: 'processing' });
      toast.success('Marked as In Review.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const recordSupplier = async (o) => {
    const supplier = window.prompt('Supplier name (e.g. Plaqueworks Co):', o.supplier_name || '');
    if (supplier === null) return;
    const confNum = window.prompt('Supplier order confirmation number:', o.supplier_confirmation_number || '');
    if (confNum === null) return;
    const note = window.prompt('Optional note (visible only to admins):', '');
    if (note === null) return;
    try {
      await api.post(`/admin/plaque-orders/${o.order_id}/supplier`, {
        supplier_name: supplier.trim() || null,
        supplier_confirmation_number: confNum.trim() || null,
        note: note.trim() || null,
      });
      toast.success('Marked as In Production.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const recordTracking = async (o) => {
    const tn = window.prompt('Tracking number:', o.tracking_number || '');
    if (tn === null || !tn.trim()) return;
    const carrier = window.prompt('Carrier (UPS / FedEx / USPS / DHL):', o.carrier || 'UPS');
    if (carrier === null) return;
    const trackUrl = window.prompt('Tracking URL (optional):', o.tracking_url || '');
    if (trackUrl === null) return;
    const eta = window.prompt('Estimated delivery date (optional, e.g. 2026-07-04):', o.estimated_delivery_date || '');
    if (eta === null) return;
    try {
      await api.post(`/admin/plaque-orders/${o.order_id}/tracking`, {
        tracking_number: tn.trim(),
        carrier: carrier.trim() || null,
        tracking_url: trackUrl.trim() || null,
        estimated_delivery_date: eta.trim() || null,
      });
      toast.success('Marked as Shipped.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resendCustomerEmail = async (o) => {
    if (!window.confirm(`Send the customer a status email for the current stage of order ${o.order_id.replace('plaque_', '')}?`)) return;
    try {
      const { data } = await api.post(`/admin/plaque-orders/${o.order_id}/notify-customer`);
      if (data.delivered) toast.success('Customer status email sent.');
      else toast.warning(`Email queued but not confirmed delivered (${data.reason || 'provider unavailable'}).`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addInternalNote = async (o) => {
    const note = window.prompt('Internal note (admins only — never emailed to the customer):', '');
    if (note === null || !note.trim()) return;
    try {
      await api.post(`/admin/plaque-orders/${o.order_id}/internal-note`, { note: note.trim() });
      toast.success('Internal note added.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markDelivered = async (o) => {
    if (!window.confirm('Confirm this order has been delivered?')) return;
    try {
      await api.post(`/admin/plaque-orders/${o.order_id}/status`, { status: 'delivered' });
      toast.success('Marked as Delivered.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cancelOrder = async (o) => {
    const note = window.prompt('Cancel/refund reason (recorded in history):', '');
    if (note === null) return;
    if (!window.confirm(`Cancel order ${o.order_id.replace('plaque_', '')}? (You will still need to issue the Stripe refund manually.)`)) return;
    try {
      await api.post(`/admin/plaque-orders/${o.order_id}/status`, {
        status: 'cancelled', note: note.trim() || null,
      });
      toast.success('Order cancelled.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markRefunded = async (o) => {
    const note = window.prompt('Refund reason / Stripe refund ID (recorded in history):', '');
    if (note === null) return;
    if (!window.confirm(`Mark order ${o.order_id.replace('plaque_', '')} as Refunded? Issue the actual refund in your Stripe dashboard first — this records it in MyLeaf.`)) return;
    try {
      await api.post(`/admin/plaque-orders/${o.order_id}/status`, {
        status: 'refunded', note: note.trim() || null,
      });
      toast.success('Order marked Refunded.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openQrArtwork = (o) => {
    if (o.qr_artwork_url) {
      window.open(o.qr_artwork_url, '_blank');
    } else {
      toast.error('No QR artwork URL captured on this order.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <header className="mb-8">
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">Platform admin</p>
          <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2" data-testid="admin-plaque-orders-title">
            Plaque orders
          </h1>
          <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-2 max-w-2xl">
            Manual fulfillment. All plaque orders are reviewed by an admin before being placed with a supplier.
          </p>
        </header>

        <div className="flex flex-wrap gap-2 mb-8">
          {STATUS_TABS.map((t) => {
            const c = counts[t.id];
            const show = (t.id !== 'all') && Number.isFinite(c);
            return (
              <button
                key={t.id}
                onClick={() => setStatusFilter(t.id)}
                className={`px-4 py-2 rounded-full text-sm border transition flex items-center gap-2 ${
                  statusFilter === t.id
                    ? 'bg-[hsl(var(--primary))] text-[hsl(var(--accent))] border-[hsl(var(--primary))]'
                    : 'border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary)/0.8)] hover:bg-[hsl(var(--primary)/0.04)]'
                }`}
                data-testid={`admin-plaque-tab-${t.id}`}
              >
                <span>{t.label}</span>
                {show && (
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                    statusFilter === t.id
                      ? 'bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))]'
                      : 'bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary)/0.7)]'
                  }`}>{c}</span>
                )}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-[hsl(var(--primary)/0.6)]">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="rounded-2xl border border-[hsl(var(--primary)/0.18)] px-7 py-10 text-center text-[hsl(var(--primary)/0.6)]" data-testid="admin-plaque-empty">
            No plaque orders in this stage.
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => {
              const isOpen = expandedId === o.order_id;
              return (
                <div
                  key={o.order_id}
                  className="rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-6 py-5"
                  data-testid={`admin-plaque-row-${o.order_id}`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
                        Order · {o.order_id.replace('plaque_', '')}
                      </div>
                      <h3 className="font-serif text-xl text-[hsl(var(--primary))] mt-1">
                        {o.quantity}× {o.product_label}
                        <span className="text-[hsl(var(--primary)/0.6)] font-sans text-sm ml-2">{money(o.amount_usd)}</span>
                      </h3>
                      <div className="text-sm text-[hsl(var(--primary)/0.7)] mt-1">
                        For <strong>{o.memorial_full_name || '—'}</strong>
                        {o.customer_name ? ` · Customer ${o.customer_name}` : ''}
                      </div>
                      <div className="text-xs text-[hsl(var(--primary)/0.55)] mt-1">
                        {o.shipping_full_name} · {o.shipping_city}, {o.shipping_state} {o.shipping_postal_code}
                      </div>
                      <div className="text-xs text-[hsl(var(--primary)/0.55)] mt-1">
                        Paid {shortDate(o.paid_at || o.created_at)}
                      </div>
                      <button
                        onClick={() => setExpandedId(isOpen ? null : o.order_id)}
                        className="mt-3 text-xs text-[hsl(var(--accent))] hover:underline"
                        data-testid={`admin-plaque-expand-${o.order_id}`}
                      >
                        {isOpen ? 'Hide details' : 'View full details'}
                      </button>

                      {isOpen && (
                        <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-xs bg-[hsl(var(--primary)/0.04)] border border-[hsl(var(--primary)/0.1)] rounded p-4" data-testid={`admin-plaque-details-${o.order_id}`}>
                          <Detail label="Order ID"          value={o.order_id} mono />
                          <Detail label="Stripe session"    value={o.stripe_session_id} mono />
                          <Detail label="Customer email"    value={o.customer_email} />
                          <Detail label="Shipping email"    value={o.shipping_email} />
                          <Detail label="Shipping phone"    value={o.shipping_phone} />
                          <Detail label="Address line 1"    value={o.shipping_address_line1} />
                          {o.shipping_address_line2 && <Detail label="Address line 2" value={o.shipping_address_line2} />}
                          <Detail label="City / State / ZIP" value={`${o.shipping_city}, ${o.shipping_state} ${o.shipping_postal_code}`} />
                          <Detail label="Country"           value={o.shipping_country} />
                          {o.engraving_text && (
                            <div className="sm:col-span-2">
                              <Detail label="Engraving / customization" value={o.engraving_text} multiline />
                            </div>
                          )}
                          <Detail label="Supplier"          value={o.supplier_name} />
                          <Detail label="Supplier conf #"   value={o.supplier_confirmation_number} mono />
                          <Detail label="Tracking number"   value={o.tracking_number} mono />
                          <Detail label="Carrier"           value={o.carrier} />
                          <Detail label="Est. delivery"     value={o.estimated_delivery_date} />
                          {o.tracking_url && (
                            <div className="sm:col-span-2">
                              <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">Tracking URL</div>
                              <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-[hsl(var(--accent))] hover:underline break-all">{o.tracking_url}</a>
                            </div>
                          )}
                          {o.notes && (
                            <div className="sm:col-span-2">
                              <Detail label="Family notes" value={o.notes} multiline />
                            </div>
                          )}
                          {Array.isArray(o.admin_notes_log) && o.admin_notes_log.length > 0 && (
                            <div className="sm:col-span-2 mt-1">
                              <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">Internal notes (admin only)</div>
                              <ul className="mt-2 space-y-1.5">
                                {o.admin_notes_log.map((n, idx) => (
                                  <li key={`${o.order_id}-n-${idx}`} className="text-[11px] text-[hsl(var(--primary)/0.85)]" data-testid={`admin-plaque-note-${o.order_id}-${idx}`}>
                                    <span className="font-mono">{(n.at || '').slice(0,10)}</span>
                                    {n.by_admin_name ? <span className="text-[hsl(var(--primary)/0.55)] ml-2">{n.by_admin_name}</span> : null}
                                    <span className="ml-2">— {n.note}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {Array.isArray(o.customer_status_emails) && o.customer_status_emails.length > 0 && (
                            <div className="sm:col-span-2 mt-1">
                              <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">Customer emails sent</div>
                              <ul className="mt-2 space-y-1.5">
                                {o.customer_status_emails.map((n, idx) => (
                                  <li key={`${o.order_id}-e-${idx}`} className="text-[11px] text-[hsl(var(--primary)/0.85)]" data-testid={`admin-plaque-email-log-${o.order_id}-${idx}`}>
                                    <span className="font-mono">{(n.at || '').slice(0,19).replace('T',' ')}Z</span>
                                    <span className="ml-2 font-medium">{plaqueStatusLabel(n.status)}</span>
                                    <span className="ml-2">→ {n.to}</span>
                                    <span className={`ml-2 ${n.delivered ? 'text-emerald-700' : 'text-red-700'}`}>{n.delivered ? 'delivered' : `not delivered${n.reason ? ` (${n.reason})` : ''}`}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {Array.isArray(o.status_history) && o.status_history.length > 0 && (
                            <div className="sm:col-span-2 mt-1">
                              <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">History</div>
                              <ul className="mt-2 space-y-1.5">
                                {o.status_history.map((h, idx) => (
                                  <li key={`${o.order_id}-h-${idx}`} className="text-[11px] text-[hsl(var(--primary)/0.85)]" data-testid={`admin-plaque-history-${o.order_id}-${idx}`}>
                                    <span className="font-mono">{(h.at || '').slice(0,19).replace('T',' ')}Z</span>
                                    <span className="ml-2 font-medium">{plaqueStatusLabel(h.status)}</span>
                                    {h.by_admin_name ? <span className="text-[hsl(var(--primary)/0.55)] ml-2">by {h.by_admin_name}</span> : null}
                                    {h.note ? <span className="ml-2">— {h.note}</span> : null}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <span
                        className={`text-xs uppercase tracking-wider px-3 py-1 rounded-full ${STATUS_PILL[o.status] || STATUS_PILL.pending_admin_fulfillment}`}
                        data-testid={`admin-plaque-status-${o.order_id}`}
                      >
                        {plaqueStatusLabel(o.status || 'pending_admin_fulfillment')}
                      </span>

                      <div className="flex flex-wrap gap-2 justify-end max-w-[300px]">
                        <button onClick={() => openQrArtwork(o)} className="ml-btn-secondary text-xs px-3 py-1.5" data-testid={`admin-plaque-qr-${o.order_id}`}>QR artwork</button>
                        <button onClick={() => resendCustomerEmail(o)} className="ml-btn-secondary text-xs px-3 py-1.5" data-testid={`admin-plaque-notify-${o.order_id}`}>Resend customer email</button>
                        <button onClick={() => addInternalNote(o)} className="ml-btn-secondary text-xs px-3 py-1.5" data-testid={`admin-plaque-note-btn-${o.order_id}`}>Internal note</button>
                        {(o.status === 'pending_admin_fulfillment') && (
                          <button onClick={() => markProcessing(o)} className="ml-btn-gold text-xs px-3 py-1.5" data-testid={`admin-plaque-process-${o.order_id}`}>Mark In Review</button>
                        )}
                        {(o.status === 'processing' || o.status === 'pending_admin_fulfillment') && (
                          <button onClick={() => recordSupplier(o)} className="ml-btn-gold text-xs px-3 py-1.5" data-testid={`admin-plaque-supplier-${o.order_id}`}>Mark In Production</button>
                        )}
                        {(o.status === 'ordered_with_supplier' || o.status === 'processing') && (
                          <button onClick={() => recordTracking(o)} className="ml-btn-gold text-xs px-3 py-1.5" data-testid={`admin-plaque-tracking-${o.order_id}`}>Mark Shipped + tracking</button>
                        )}
                        {(o.status === 'shipped') && (
                          <button onClick={() => markDelivered(o)} className="ml-btn-primary text-xs px-3 py-1.5" data-testid={`admin-plaque-delivered-${o.order_id}`}>Mark Delivered</button>
                        )}
                        {(o.status !== 'delivered' && o.status !== 'cancelled' && o.status !== 'refunded') && (
                          <button onClick={() => cancelOrder(o)} className="ml-btn-secondary text-xs px-3 py-1.5 text-red-700" data-testid={`admin-plaque-cancel-${o.order_id}`}>Cancel</button>
                        )}
                        {(o.payment_status === 'paid' && o.status !== 'refunded') && (
                          <button onClick={() => markRefunded(o)} className="ml-btn-secondary text-xs px-3 py-1.5 text-gray-600" data-testid={`admin-plaque-refund-${o.order_id}`}>Mark Refunded</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <LegacyRequestsSection />
      </AdminLayout>
    </div>
  );
}

function LegacyRequestsSection() {
  const [rows, setRows] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/qr-orders/legacy-audit')
      .then(({ data }) => setRows(data.rows || []))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!rows || rows.length === 0) return null;
  const open = rows.filter((r) => r.status !== 'cancelled' && r.status !== 'completed');

  const cancel = async (r) => {
    if (!window.confirm(`Cancel legacy request ${r.order_id}? The customer was never charged.`)) return;
    try {
      await api.post(`/admin/qr-orders/${r.order_id}/cancel`);
      toast.success('Legacy request cancelled.');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendLink = async (r) => {
    if (!window.confirm(`Email ${r.customer_email} a secure checkout link with their details pre-filled?`)) return;
    try {
      const { data } = await api.post(`/admin/qr-orders/${r.order_id}/send-checkout-link`);
      if (data.delivered) toast.success('Checkout link emailed to the customer.');
      else toast.warning('Link created but email delivery failed.');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="mt-12 rounded-2xl border border-amber-300 bg-amber-50/40 overflow-hidden" data-testid="legacy-requests-section">
      <header className="px-6 py-4 border-b border-amber-200">
        <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Legacy unpaid requests</h2>
        <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-1">
          Submitted through the old no-charge request form (now retired). <strong>None of these ever
          created a Stripe checkout — no customer was charged.</strong> Cancel each request, or email
          the customer a secure checkout link with their details pre-filled.
        </p>
      </header>
      <div className="divide-y divide-amber-200/60">
        {rows.map((r) => (
          <div key={r.order_id} className="px-6 py-4 flex flex-wrap items-center gap-3" data-testid={`legacy-request-${r.order_id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-[hsl(var(--primary))]">
                {r.customer_name || '—'} <span className="text-[hsl(var(--primary)/0.55)] text-sm">· {r.customer_email || 'no email'}</span>
                <span className={`ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold ${r.status === 'cancelled' ? 'bg-gray-200 text-gray-600' : r.checkout_link_sent_at ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>
                  {r.status === 'cancelled' ? 'Cancelled' : r.checkout_link_sent_at ? 'Checkout link sent' : 'Never charged'}
                </span>
              </div>
              <div className="text-xs text-[hsl(var(--primary)/0.6)] mt-0.5">
                {r.memorial_name ? `${r.memorial_name} · ` : ''}{r.quantity || 1}× {r.requested_product || '—'} · requested {shortDate(r.created_at)}
                {r.shipping_address ? ` · ships to ${r.shipping_address}` : ''}
              </div>
              <div className="text-[10px] text-[hsl(var(--primary)/0.5)] mt-0.5 italic">{r.determination}</div>
            </div>
            {r.status !== 'cancelled' && (
              <div className="flex gap-2">
                {r.customer_email && (
                  <button onClick={() => sendLink(r)} className="ml-btn-gold text-xs px-3 py-1.5" data-testid={`legacy-send-link-${r.order_id}`}>
                    Send checkout link
                  </button>
                )}
                <button onClick={() => cancel(r)} className="ml-btn-secondary text-xs px-3 py-1.5 text-red-700" data-testid={`legacy-cancel-${r.order_id}`}>
                  Cancel request
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      {open.length === 0 && (
        <div className="px-6 py-3 text-xs text-[hsl(var(--primary)/0.55)]">All legacy requests have been resolved.</div>
      )}
    </section>
  );
}

function Detail({ label, value, mono, multiline }) {
  return (
    <div>
      <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">{label}</div>
      <div className={`text-[hsl(var(--primary))] mt-1 ${mono ? 'font-mono break-all' : ''} ${multiline ? 'whitespace-pre-wrap' : ''}`}>
        {value || '—'}
      </div>
    </div>
  );
}
